/* eslint-disable class-methods-use-this */
import debounce from 'lodash/debounce';
import {axaStore} from 'Store/index';
import {
    updateStepBlockModel,
    updateStepBlockSlideId,
    removeLastStepBlockSlideId,
    removeLastStepBlockModel
} from 'Store/stepsBlock/stepsBlock.action';
import {STEPSBLOCK_REDUCER} from 'Store/reducer';

/**
 * @description Allow the user to navigate through step on the element.
 */
export default class StepsBlock {
    /**
     * @description Create a Steps Blocks - Multiple steps
     * component with a shared Model.
     * @param {HTMLElement} domElement - The steps block element.
     */
    constructor(domElement) {
        if(!domElement) {
            throw new Error('element is undefined');
        }

        const isInitialized = domElement.getAttribute('data-steps-block-initialized') === 'true';

        if(isInitialized) {
            throw new Error('Module already initialized');
        }

        this.domElement = domElement;
        this.domElement.dataset.stepsBlockInitialized = 'true';
        this.debounceTime = 200;

        this.config = {
            /**
             * @description Getter for the steps.
             * @returns {Array|HTMLElement} All the steps.
             */
            get _steps() {
                return [...this.container.querySelectorAll('.js-step')];
            },
            container: domElement.querySelector('.js-block-business-content'),

            /**
             * @description The from containers the number of steps.
             * @returns {number} The number of steps available.
             */
            get nbSteps() {
                return this._steps.length;
            },
            steps: {}
        };

        window.addEventListener(
            'resize',
            debounce(this.refreshStep.bind(this, false), this.debounceTime)
        );
    }

    /**
     * @description Update model.
     * @param {object} value - Value to be updated/added to the model.
     */
    addModel(value) {
        axaStore.dispatch(updateStepBlockModel({dataModel: value}));
    }

    /**
     * @description Read model.
     * @param {string} value - Value to be read from model.
     * @returns {string} Item from the model object.
     */
    get(value) {
        return this.getModel()[value];
    }

    getModel() {
        const {model: modelList} = axaStore.getState()[STEPSBLOCK_REDUCER];
        return modelList[modelList.length - 1];
    }

    getActiveStep() {
        const {activeStep: activeStepList} = axaStore.getState()[STEPSBLOCK_REDUCER];
        return activeStepList[activeStepList.length - 1];
    }

    setActiveStep(step) {
        axaStore.dispatch(updateStepBlockSlideId({activeStep: step}));
    }

    /**
     * @description Create a map with the steps names
     * used mostly for analytics.
     */
    mapSteps() {
        this.config.steps = this.config._steps
            .reduce((accumulateur, item, index) => ({
                ...accumulateur,
                [index + 1]: item.getAttribute('data-step-name')
            }), {});
    }

    /**
     * @description To previous specific step.
     * @param {number} idx - The index of the step to go to.
     */
    goToPreviousStep(idx) {
        const {activeStep: activeStepList} = axaStore.getState()[STEPSBLOCK_REDUCER];
        if(activeStepList.length > 1) {
            this.goToStep(idx);
        }
    }

    /**
     * @description Go to next step.
     */
    nextStep() {
        if(this.getActiveStep() < this.config.nbSteps) {
            this.setActiveStep(this.getActiveStep() + 1);
            this.refreshStep();
        }
    }

    /**
     * @description Go to previous step.
     */
    previousStep() {
        if(this.getActiveStep() > 1) {
            this.setActiveStep(this.getActiveStep() - 1);
            this.refreshStep();
        }
    }

    backStep() {
        const {activeStep: activeStepList, model: modelList} = axaStore.getState()[STEPSBLOCK_REDUCER];

        if(activeStepList.length < 2 && modelList.length < 2) {
            return;
        }

        axaStore.dispatch(removeLastStepBlockSlideId());
        axaStore.dispatch(removeLastStepBlockModel());
        this.refreshStep();
    }

    /**
     * @description Go to specific step.
     * @param {number} stepIndex - Update the active index.
     */
    goToStep(stepIndex) {
        this.setActiveStep(Number.parseInt(stepIndex, 0));
        this.refreshStep();
    }

    /**
     * @description Find the index of specific step.
     * @param {string} step - Name of the step we are looking for.
     * @returns {number} Index of the step we passed in argument.
     */
    findStepIndex(step) {
        const index = this.config._steps.findIndex(item => item === step);
        return index !== -1 ? index + 1 : 1;
    }

    /**
     * @description Go to the last step.
     */
    firstStep() {
        this.setActiveStep(1);
        this.refreshStep();
    }

    /**
     * @description Go to the last step.
     */
    lastStep() {
        this.setActiveStep(this.config.nbSteps);
        this.refreshStep();
    }

    /**
     * @description Will update position of the steps block container,
     * show the active step and hide the other steps
     * so they can't receive focus, give the focus to the active item,
     * and update TCVars each time the active step is updated.
     * @param {boolean} handleFocus - Active the focus on the item.
     */
    refreshStep(handleFocus = true) {
        const {container, _steps} = this.config;

        const marginDivideNumber = 2;
        const itemWidth = (
            container.parentNode || this.domElement.querySelector('.js-block-business-container')
        ).clientWidth - container.clientWidth;

        const itemIndex = this.getActiveStep() - 1;
        const stepMarginRight = itemWidth / marginDivideNumber;
        container.style.transform = `translateX(-${itemIndex * (container.clientWidth + stepMarginRight)}px)`;

        _steps.forEach((item, index) => {
            const itemStyle = item;

            itemStyle.style.visibility = index === itemIndex ? 'visible' : 'hidden';
            itemStyle.style.marginRight = stepMarginRight;
        });

        if(handleFocus && this.domElement.contains(document.activeElement)) {
            setTimeout(this.setStepFocusByIndex.bind(this, itemIndex), 300);
        }

        this.updateTCVars();
    }

    /**
     * @description It will set focus on the step by index.
     * @param {number} itemIndex - Index of the step.
     */
    setStepFocusByIndex(itemIndex) {
        this.config._steps[itemIndex].focus();
    }

    /**
     * @description It will update TCVars when needed,
     * mostly used on each Business Block.
     */
    updateTCVars() {
        if(!window.tc_vars) {
            window.tc_vars = {};
        }
    }

    /**
     * @description Helper function to toggle className.
     * @param {string} className - Class to be toggled.
     * @param {HTMLElement} domElement - DOM element to be treated.
     */
    toggleElementClass(className, domElement) {
        domElement.classList.toggle(className);
    }

    /**
     * @description Helper function to remove class
     *  from all elements inside an scope.
     * @param {string} className - Class to be removed.
     * @param {HTMLElement} scope - DOM element that contain
     * the items to be treated.
     */
    removeClassFromScope(className, scope) {
        const itemScope = scope || document;
        const domItemsList = [...itemScope.querySelectorAll(`.${className}`)];
        domItemsList.forEach(domElement => domElement
            .classList
            .remove(className));
    }

    /**
     * @description Helper function to hide an element.
     * @param {string} selector - Selector of the element to be hidden.
     */
    hideElement(selector) {
        const domElement = this.domElement.querySelector(selector);

        if(domElement) {
            domElement.style.display = 'none';
        }
    }
}
